import type { AgentType } from '@ardoq/api-types';
import {
  type AssistantChatMessage,
  type ChatMessage,
  type UserChatMessage,
  isAssistantChatMessage,
  isUserChatMessage,
} from './types';

export const getLastMessage = (
  messages: ChatMessage[]
): ChatMessage | undefined => messages.at(-1);

export const getLastUserMessage = (
  messages: ChatMessage[]
): UserChatMessage | undefined => messages.findLast(isUserChatMessage);

export const getLastAssistantMessage = (
  messages: ChatMessage[]
): AssistantChatMessage | undefined =>
  messages.findLast(isAssistantChatMessage);

/**
 * Returns the agent type a conversation should continue with, taken from the
 * most recent assistant message carrying one (excluding the single-shot `report_insight_with_asset_context`).
 *
 * @remarks
 * Transitional helper. Tracking a hardcoded `agentType` on the client is not the
 * intended long-term design — agent selection and routing are expected to move
 * server-side, with the LLM orchestrating custom agents, so the UI no longer
 * needs to know a fixed set of agent identifiers. It exists to support the
 * current product-tour, and insight agent integration while the assistant and custom-agent support
 * are still early, and should be used sparingly and removed once that routing
 * lands.
 */
export const getAgentType = (
  messages: ChatMessage[]
): 'product_tour' | undefined => {
  const agentType = messages.findLast(
    (message): message is AssistantChatMessage & { agentType: AgentType } =>
      isAssistantChatMessage(message) && Boolean(message.agentType)
  )?.agentType;
  return agentType !== 'report_insight_with_asset_context'
    ? agentType
    : undefined;
};
